// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * Drawing and hit geometry for a date track — the strip that says *when*
 * the frame on the globe is, and where in the span the playhead sits.
 *
 * Two surfaces draw this: the in-VR plane (`vrTimelineTrack`, a
 * CanvasTexture on a Three.js plane) and the 2D playback panel
 * (`src/ui/timelineTrackUI`, a plain canvas in the transport). They
 * share this module rather than a copy each, because a track that
 * disagreed with the other about where a tick falls or what a shaded
 * span means would be two different claims about the same data.
 *
 * The axis itself is not here: it is parsed in `dsaTimeline.ts` from
 * the stream's `.dsa`. This module takes the resulting
 * {@link TimelineTrackState} and draws it.
 *
 * **Geometry is proportional, never absolute.** Every inset, font size
 * and bar position is a fraction of the canvas, so one implementation
 * covers a 1200 x 220 VR strip and a 280 x 56 panel canvas, and the
 * caller only picks the pixel size. The fractions are the VR numbers
 * divided by 1200/220, which is why the VR drawing did not move when
 * this was extracted.
 *
 * **Compact below 480 px.** A phone-width transport cannot carry a
 * header row, seven tick labels and a date bubble; compact mode drops
 * the header, keeps the ends labelled under the bar, and lets the
 * surrounding UI (the 2D panel's own time label, or the VR HUD's) carry
 * the current instant. The bar, the shaded provenance spans and the
 * playhead survive at every size, because those are the three things the
 * strip exists to say.
 *
 * Dates render in **UTC**, matching the axis and the MPD's
 * `availabilityStartTime`; a tick labelled in the viewer's device
 * timezone would move under a stream that did not.
 *
 * See {@link file://./../../docs/VR_PLAYBACK_TRACK_PLAN.md VR_PLAYBACK_TRACK_PLAN.md}.
 */
import { getLocale } from '../i18n'

import {
  createFrameAxis,
  formatFrameLabel,
  frameAtTimeMs,
  frameTimeMs,
  lastFrameTimeMs,
  precisionForCadence,
  type FrameAxis,
} from './frameCalendar'

// ---------------------------------------------------------------------------
// State and geometry
// ---------------------------------------------------------------------------

/** How the DSA's provenance block describes one span — the subset the drawing needs. */
export interface TimelineAvailabilitySpan {
  readonly startFrame: number
  readonly frameCount: number
  readonly availability: 'real' | 'filled' | 'missing' | 'estimated' | 'interpolated' | 'unknown'
}

/** Everything the strip needs to draw one frame of the axis. */
export interface TimelineTrackState {
  /** Instant of frame 0, ms since epoch. */
  readonly startMs: number
  /** Exclusive end of the axis (the instant after the final frame). */
  readonly endMs: number
  /** Instant the playhead represents, ms since epoch. */
  readonly currentMs: number
  readonly frameCount: number
  readonly cadenceMs: number
  /** Sparse provenance spans; a frame inside none of them is ordinary data. */
  readonly availabilitySpans: readonly TimelineAvailabilitySpan[]
  /** True while a scrub is in flight — brightens the playhead and frame. */
  readonly scrubbing: boolean
  /**
   * AYNI: the strip's colour. 'base' (green, the default) is the dataset on
   * the globe; 'overlay' (yellow) is a real-time overlay drawn on the
   * dataset's axis, under the green strip.
   */
  readonly accent?: TimelineAccent
  /**
   * AYNI: the dates the strip's subject has data for, on this axis. Outside
   * them the bar is hatched and the playhead hollow (the overlay is hidden
   * on the globe there). Absent: the whole axis is covered.
   */
  readonly coverage?: { readonly startMs: number; readonly endMs: number } | null
  /**
   * AYNI: the frames in time (`frameCalendar.ts`), when the caller has the
   * stream's own (a declared calendar or per-frame stamps). Absent: built
   * from `startMs`, `frameCount` and `cadenceMs`, which is the same axis
   * for every stream the host publishes today.
   */
  readonly axis?: FrameAxis
  /**
   * AYNI (satellites ↔ catalog plan, step 2): which satellite supplied each
   * period of the dataset (DSA 1.10 platforms), drawn as a thin strip over
   * the bar and named by the hover. Absent or empty: no strip.
   */
  readonly bands?: readonly TimelineBand[]
}

/** One satellite's period on the axis; null ends are the axis's own. */
export interface TimelineBand {
  readonly startMs: number | null
  readonly endMs: number | null
  readonly label: string
  /** The window is a whole year that overlaps another's (GOES generations): said to be about right. */
  readonly approximate?: boolean
}

/** The bands holding an instant, in the order written (several when hand-overs overlap). */
export function bandsAt(bands: readonly TimelineBand[] | undefined, ms: number): TimelineBand[] {
  if (!bands?.length) return []
  return bands.filter((b) => (b.startMs === null || ms >= b.startMs) && (b.endMs === null || ms < b.endMs))
}

/**
 * AYNI: the state's frames in time. Every frame↔date on the strip goes
 * through this, so a monthly axis steps calendar months here exactly as
 * it does in `dsaTimeline` (the seek) and the readout over the globe.
 */
export function trackAxis(state: TrackAxisSource): FrameAxis {
  return state.axis ?? createFrameAxis({ startMs: state.startMs, frameCount: state.frameCount, cadenceMs: state.cadenceMs })
}

/** What {@link trackAxis} reads: any record of an axis (the track's, the headset's, the phone's). */
export type TrackAxisSource = Pick<TimelineTrackState, 'startMs' | 'frameCount' | 'cadenceMs' | 'axis'>

/** AYNI: the last frame's stamp — where an end label, a pin graph or a CSV stops. */
export function lastFrameMsOf(state: TrackAxisSource): number {
  return lastFrameTimeMs(trackAxis(state))
}

/**
 * AYNI: the date `delta` frames from the frame holding `ms`, clamped to
 * the axis — the arrow keys' step. By frame, not by adding the cadence:
 * a mean month added to a stamp on the 31st can skip February.
 */
export function stepFrameMs(state: TimelineTrackState, ms: number, delta: number): number {
  const axis = trackAxis(state)
  return frameTimeMs(axis, frameAtTimeMs(axis, ms) + delta)
}

/** AYNI: which strip this is, which sets its colour. */
export type TimelineAccent = 'base' | 'overlay'

/** A canvas's layout, in its own pixels. Derive it; never hard-code it. */
export interface TimelineTrackGeometry {
  readonly width: number
  readonly height: number
  /** The bar the playhead rides, in canvas pixels. */
  readonly bar: { readonly x: number; readonly y: number; readonly w: number; readonly h: number }
  /** True when the canvas is too small for a header row and a bubble. */
  readonly compact: boolean
  /** Font size for the header row, in canvas pixels. */
  readonly headerFontPx: number
  /** Font size for tick labels and the end dates. */
  readonly tickFontPx: number
  /** Font size for the playhead's instant bubble. */
  readonly bubbleFontPx: number
}

/** Below this width the header row and the playhead bubble are dropped. */
export const COMPACT_WIDTH_PX = 480

/**
 * Minimum gap between seeks while a scrub is in flight, for every surface
 * that has one. A seek stalls a decoder, and a pointer drag or a
 * controller emits far more events a second than a decoder can answer;
 * the drawn playhead follows the input every frame regardless, so the
 * video catches up in steps while the strip stays smooth. One constant
 * for both, because it is one hardware problem: thirty a second was
 * visibly choppy on a projector and ninety was a permanent stall.
 */
export const SCRUB_SEEK_INTERVAL_MS = 120

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR
/** The mean Gregorian year — the cadence annual streams (CMIP6 projections) declare. */
const YEAR = 365.2425 * DAY

/**
 * An axis whose frames are a whole number of years apart is read by year.
 * Its origin is placed so each frame lands inside its own year (the CMIP6
 * projections start at 1950-01-02T10:00Z), so a day or a time on it is
 * that offset, not data: "Jan 2, 2075" reads as a date the frame stands
 * for, and it doesn't. AYNI: the rule is `frameCalendar`'s (within 6 %
 * of whole years), the one that also steps the frames by calendar years.
 */
export function isAnnualCadence(cadenceMs: number): boolean {
  return precisionForCadence(cadenceMs) === 'year'
}

/** `2075`: the UTC year of an instant. */
export function formatYear(ms: number): string {
  return String(new Date(ms).getUTCFullYear())
}

/** The mean Gregorian month (2 629 746 s) — the cadence monthly streams declare. */
const MONTH = YEAR / 12

/**
 * An axis whose frames are 1, 2, 3, 4 or 6 calendar months apart is read
 * by month, for the same reason an annual one is read by year: the day
 * and time of a frame are the publisher's offset, not data. AYNI: the
 * rule is `frameCalendar`'s (within 6 % of those months), the one that
 * also steps the frames by calendar months; a 28-day or 45-day cadence is
 * a fixed step and is read by date.
 */
export function isMonthlyCadence(cadenceMs: number): boolean {
  return precisionForCadence(cadenceMs) === 'month'
}

/** `Feb 2024`: the UTC month of an instant, as the desktop apps write it. */
export function formatMonth(ms: number): string {
  return new Date(ms).toLocaleDateString(getLocale(), { month: 'short', year: 'numeric', timeZone: 'UTC' })
}

/**
 * The VR strip's original pixel geometry, kept as the numerator of every
 * fraction below. Writing 118/220 rather than 0.536 is deliberate: a
 * rounded fraction moves the shipped VR drawing by a fraction of a pixel,
 * which is invisible and makes "the geometry did not change" a claim
 * nobody can check.
 */
const REF_W = 1200
const REF_H = 220

/**
 * Layout for a canvas of this size. The fractions are the original VR
 * strip's pixels over its 1200 x 220 canvas, so that surface renders
 * exactly as it did before both consumers shared this code.
 */
export function timelineTrackGeometry(
  width: number,
  height: number,
): TimelineTrackGeometry {
  const w = Math.max(1, Math.round(width))
  const h = Math.max(1, Math.round(height))
  const compact = w < COMPACT_WIDTH_PX
  return {
    width: w,
    height: h,
    bar: {
      x: (36 / REF_W) * w,
      // Compact trades the header row's space for the bar: same canvas
      // height, no room for a date above it.
      y: ((compact ? 66 : 118) / REF_H) * h,
      w: (1128 / REF_W) * w,
      h: ((compact ? 66 : 52) / REF_H) * h,
    },
    compact,
    // Floored at 9 px: a magnified sub-pixel label is worse than a
    // cramped one, and a transport strip is read at a glance.
    headerFontPx: Math.max(9, (26 / REF_W) * w),
    tickFontPx: Math.max(9, (22 / REF_W) * w),
    bubbleFontPx: Math.max(10, (28 / REF_W) * w),
  }
}

// ---------------------------------------------------------------------------
// Progress mapping (the hit geometry)
// ---------------------------------------------------------------------------

/** Axis progress 0 -> 1 for a canvas x, or null outside the bar. */
export function progressAtCanvasX(x: number, geometry: TimelineTrackGeometry): number | null {
  const { bar } = geometry
  if (x < bar.x || x > bar.x + bar.w) return null
  return (x - bar.x) / bar.w
}

/** Canvas x for an axis progress — the inverse of {@link progressAtCanvasX}. */
export function progressToCanvasX(progress: number, geometry: TimelineTrackGeometry): number {
  const clamped = Math.max(0, Math.min(1, progress))
  return geometry.bar.x + clamped * geometry.bar.w
}

/**
 * Axis progress for a UV point on a Three.js plane carrying this canvas,
 * or null when it missed the bar.
 *
 * UV is the plane's space, not the canvas's: `u` runs 0 -> 1 left to
 * right and `v` runs 0 -> 1 **bottom to top**, while canvas y runs
 * top to bottom, so `v` is flipped here. Getting that wrong is how a
 * hit test lands on the mirror image of the point the user aimed at.
 */
export function uvToProgress(
  uv: { x: number; y: number },
  geometry: TimelineTrackGeometry,
): number | null {
  const x = uv.x * geometry.width
  const y = (1 - uv.y) * geometry.height
  const { bar } = geometry
  if (y < bar.y || y > bar.y + bar.h) return null
  return progressAtCanvasX(x, geometry)
}

// ---------------------------------------------------------------------------
// Labels
// ---------------------------------------------------------------------------

/**
 * Tick intervals the ladder may land on, in ms. Reaches weeks and months
 * because a real-time window runs a month and a forecast runs a day; the
 * steps are coarse enough that a label is never cramped.
 */
export const TICK_INTERVALS_MS: readonly number[] = [
  MINUTE,
  5 * MINUTE,
  15 * MINUTE,
  30 * MINUTE,
  HOUR,
  3 * HOUR,
  6 * HOUR,
  12 * HOUR,
  DAY,
  2 * DAY,
  3 * DAY,
  7 * DAY,
  14 * DAY,
  30 * DAY,
  90 * DAY,
  YEAR,
  // Multi-year steps, for annual streams spanning a century and more
  // (1950–2100 is 151 frames). Placed on calendar years, not by adding
  // milliseconds — see `tickTimesFor`.
  2 * YEAR,
  5 * YEAR,
  10 * YEAR,
  20 * YEAR,
  25 * YEAR,
  50 * YEAR,
  100 * YEAR,
]

/**
 * Where the ticks fall inside `(startMs, endMs)`. Steps under a year are
 * multiples of the step from the epoch, as they have always been. Steps
 * of a year or more land on January 1 of every Nth year (1960, 1970, …):
 * counting years in milliseconds from 1970 drifts about a quarter of a
 * day a year off the calendar, and a year-only label must name the year
 * its tick is actually in.
 *
 * On a monthly axis (pass its cadence), steps under a year land on the
 * 1st of every Nth month for the same reason: a multiple of 30 days
 * from the epoch names some months twice and skips others.
 */
export function tickTimesFor(startMs: number, endMs: number, intervalMs: number, cadenceMs = 0): number[] {
  const spanMs = endMs - startMs
  const times: number[] = []
  const push = (ms: number) => {
    const p = (ms - startMs) / spanMs
    if (p > 0.001 && p < 0.999) times.push(ms)
  }
  if (intervalMs >= 365 * DAY) {
    const everyYears = Math.max(1, Math.round(intervalMs / YEAR))
    const firstYear = Math.ceil(new Date(startMs).getUTCFullYear() / everyYears) * everyYears
    for (let year = firstYear; ; year += everyYears) {
      const ms = Date.UTC(year, 0, 1)
      if (ms >= endMs) break
      push(ms)
    }
    return times
  }
  if (isMonthlyCadence(cadenceMs)) {
    const everyMonths = Math.max(1, Math.round(intervalMs / MONTH))
    const start = new Date(startMs)
    const startIndex = start.getUTCFullYear() * 12 + start.getUTCMonth()
    for (let index = Math.ceil(startIndex / everyMonths) * everyMonths; ; index += everyMonths) {
      const ms = Date.UTC(Math.floor(index / 12), index % 12, 1)
      if (ms >= endMs) break
      push(ms)
    }
    return times
  }
  for (let ms = Math.ceil(startMs / intervalMs) * intervalMs; ms < endMs; ms += intervalMs) push(ms)
  return times
}

/**
 * Smallest ladder interval that keeps the tick count at or under
 * `maxTicks`. Falls back to the largest interval when even that is too
 * dense — the caller draws what it gets rather than dropping labels
 * mid-axis.
 */
export function chooseTickIntervalMs(spanMs: number, maxTicks = 7): number {
  const largest = TICK_INTERVALS_MS[TICK_INTERVALS_MS.length - 1]!
  if (!(spanMs > 0) || maxTicks < 1) return largest
  for (const interval of TICK_INTERVALS_MS) {
    if (spanMs / interval <= maxTicks) return interval
  }
  return largest
}

/**
 * How many tick labels a strip of this width can carry. Roughly one per
 * 150 px, floored at 2 so an axis always shows its middle, and capped at
 * 7 as the VR ladder assumed.
 */
export function tickBudgetForWidth(width: number): number {
  return Math.max(2, Math.min(7, Math.round(width / 150)))
}

/** One label's horizontal extent on its row, in canvas pixels. */
export interface TimelineLabelBox {
  /** Position in the row's own order — what the caller draws by. */
  readonly index: number
  readonly left: number
  readonly right: number
}

/**
 * Which labels survive on one row, left to right, given a minimum gap.
 *
 * The ladder picks a *count*, but a count is not a fit: the labels are
 * dates whose width changes with the span ("Aug 18" against "Aug 2026"),
 * the canvas changes with the panel, and a ladder that tops out can still
 * return more ticks than the budget asked for (a ten-year axis falls
 * through to its largest step and lands ten labels on a phone). Measuring
 * and dropping is the only rule that holds for all three at once.
 *
 * Greedy from the left, because the first label on a row is the one a
 * reader anchors on, and because a dropped label costs a tick's *text*
 * and never its tick line — the marks stay, the axis stays readable, and
 * the spacing stays even to the eye.
 *
 * @param boxes    Label extents, any order; sorted here.
 * @param minGapPx Clear space to leave between two kept labels.
 * @param anchored Indices that must survive — an axis's ends, which are
 *   what the row is *for*; a candidate that would touch one is dropped
 *   instead.
 * @returns The `index`es to draw.
 */
export function distributeLabels(
  boxes: readonly TimelineLabelBox[],
  minGapPx: number,
  anchored: readonly number[] = [],
): number[] {
  const anchoredSet = new Set(anchored)
  const kept = boxes.filter(box => anchoredSet.has(box.index) && box.right >= 0)
  const keptIndexes = kept.map(box => box.index)
  const candidates = boxes
    .filter(box => !anchoredSet.has(box.index) && box.right >= 0)
    .sort((a, b) => a.left - b.left)
  for (const box of candidates) {
    // Against every kept box, not just the last: with two anchored ends the
    // row has a hole in the middle, and a candidate must clear both.
    const collides = kept.some(
      k => box.left < k.right + minGapPx && k.left < box.right + minGapPx,
    )
    if (collides) continue
    kept.push(box)
    keptIndexes.push(box.index)
  }
  keptIndexes.sort((a, b) => a - b)
  return keptIndexes
}

/** Clear space between two labels on a row: a share of the width, with a
 *  floor, because at 200 px a proportional gap rounds to nothing. */
function labelGapPx(width: number): number {
  return Math.max(6, 0.006 * width)
}

/** Compact cadence label: `15m`, `1h`, `6h`, `1d`. Machine-ish on
 *  purpose — the strip's words are numbers, not prose. */
export function formatCadenceShort(cadenceMs: number): string {
  if (!(cadenceMs > 0)) return '-'
  // Sub-minute cadences are real (a one-second satellite loop) and would
  // round to "0m" — a label that says the axis has no step at all.
  if (cadenceMs < MINUTE) return String(Math.max(1, Math.round(cadenceMs / 1000))) + 's'
  if (cadenceMs < HOUR) return String(Math.round(cadenceMs / MINUTE)) + 'm'
  if (cadenceMs < DAY) {
    const hours = cadenceMs / HOUR
    return String(Number.isInteger(hours) ? hours : hours.toFixed(1)) + 'h'
  }
  if (isAnnualCadence(cadenceMs)) {
    // "1y", not "365.2d": the mean Gregorian year is not a whole number
    // of days, and the step is a year.
    const years = Math.round((cadenceMs / YEAR) * 10) / 10
    return String(years) + 'y'
  }
  if (isMonthlyCadence(cadenceMs)) {
    // "1mo", not "30.4d", for the same reason. A calendar month (28–31
    // days) is one month too.
    const months = cadenceMs / MONTH
    const whole = Math.round(months)
    return String(Math.abs(months - whole) < 0.1 ? whole : Math.round(months * 10) / 10) + 'mo'
  }
  const days = cadenceMs / DAY
  return String(Number.isInteger(days) ? days : days.toFixed(1)) + 'd'
}

/**
 * A tick label: `Aug 18` inside one year, `Aug 2026` across a longer
 * span. UTC and the active language, matching the app's other date labels except for
 * the timezone — see the module docstring.
 */
export function formatTickDate(ms: number, spanMs: number): string {
  const opts: Intl.DateTimeFormatOptions =
    spanMs > 300 * DAY
      ? { month: 'short', year: 'numeric', timeZone: 'UTC' }
      : { month: 'short', day: 'numeric', timeZone: 'UTC' }
  return new Date(ms).toLocaleDateString(getLocale(), opts)
}

/**
 * A label on the axis, with the precision its *step* deserves.
 *
 * The form follows the step, not the axis: a six-hour axis stepped every
 * three hours and a thirty-hour axis stepped every six both land several
 * labels on the same day, and a row that says "Aug 18" four times explains
 * nothing. So a step shorter than a day prints the day **and the time**,
 * and anything longer prints the day — or the month and year once the span
 * runs past a year, where the day would be noise.
 *
 * The ends are their own case: their step is the whole span, so a six-hour
 * axis labels its ends with times for the same reason.
 */
export function formatAxisLabel(ms: number, stepMs: number, spanMs: number, cadenceMs = 0): string {
  // A tick every year or more sits on January 1 (`tickTimesFor`): the
  // year is the whole of what it says.
  if (stepMs >= 365 * DAY && stepMs < spanMs) return formatYear(ms)
  // On a monthly axis a shorter step sits on the 1st of a month, and the
  // month is what it says, however short the axis.
  if (isMonthlyCadence(cadenceMs)) return formatMonth(ms)
  if (stepMs < DAY) {
    return new Date(ms).toLocaleString(getLocale(), {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'UTC',
    })
  }
  return formatTickDate(ms, spanMs)
}

/**
 * An end of the axis: the span is the step. See {@link formatAxisLabel}.
 * An annual axis is labelled by year and a monthly one by month; pass
 * its cadence.
 */
export function formatAxisEndLabel(ms: number, spanMs: number, cadenceMs = 0): string {
  if (isAnnualCadence(cadenceMs)) return formatYear(ms)
  if (isMonthlyCadence(cadenceMs)) return formatMonth(ms)
  return formatAxisLabel(ms, spanMs, spanMs)
}

/**
 * The instant the axis's right-hand label names. The axis ends where its
 * last frame's cell does; on an annual axis that is early in the year
 * *after* the last frame (1950–2100 ends at 2101-01-02), so it names the
 * last frame instead — "2100", the year the data runs to. A monthly
 * axis does the same: it ends early in the month after its last frame.
 */
function axisEndLabelMs(state: TimelineTrackState): number {
  return isAnnualCadence(state.cadenceMs) || isMonthlyCadence(state.cadenceMs)
    ? lastFrameTimeMs(trackAxis(state))
    : state.endMs
}

/** The instant a playhead label shows: the date alone on a daily axis,
 *  date and time when the cadence is sub-daily (a bare date at 15-minute
 *  steps is useless). */
export function formatPlayheadLabel(ms: number, cadenceMs: number): string {
  // A frame of an annual stream stands for its year, and one of a
  // monthly stream for its month. See isAnnualCadence, isMonthlyCadence.
  // AYNI: one formatter for every surface (frameCalendar.formatFrameLabel).
  return formatFrameLabel(ms, precisionForCadence(cadenceMs), getLocale())
}

/**
 * AYNI: what a pointer resting on the strip is aiming at — the frame a
 * press there would land on, and what is known about that frame.
 *
 * `note` is the reason the frame is not an ordinary measurement, or null:
 * `uncovered` where an overlay strip's subject has no data (the hatched
 * stretch), otherwise the provenance span the frame sits in (`missing`,
 * `filled`, or `estimated` for estimated and interpolated frames alike,
 * as the drawing colours them alike).
 */
export interface TimelineHoverReadout {
  /** The instant of the frame under the pointer, ms since epoch. */
  readonly ms: number
  readonly note: 'uncovered' | 'missing' | 'filled' | 'estimated' | null
  /** AYNI: the satellites that supplied this frame's period, when the state has bands. */
  readonly bands: readonly TimelineBand[]
}

/**
 * AYNI: the readout for an axis progress (0 -> 1, as
 * {@link progressAtCanvasX} gives it).
 *
 * Snapped the way a seek snaps (`frameAtDateMs`: the nearest frame,
 * clamped to the axis), so the date the pointer promises is the date a
 * click delivers, not an instant between two frames.
 */
export function hoverReadoutAt(state: TimelineTrackState, progress: number): TimelineHoverReadout {
  const spanMs = state.endMs - state.startMs
  const p = Math.max(0, Math.min(1, Number.isFinite(progress) ? progress : 0))
  const raw = state.startMs + p * spanMs
  let ms = raw
  let frame = -1
  if (state.cadenceMs > 0 && state.frameCount > 0) {
    // AYNI: through the frame calendar, as the seek goes (frameAtDateMs).
    const axis = trackAxis(state)
    frame = frameAtTimeMs(axis, raw)
    ms = frameTimeMs(axis, frame)
  }
  const bands = bandsAt(state.bands, ms)
  const cover = state.coverage ?? null
  if (cover && (ms < cover.startMs || ms > cover.endMs)) return { ms, note: 'uncovered', bands }
  if (frame >= 0) {
    for (const span of state.availabilitySpans) {
      if (frame < span.startFrame || frame >= span.startFrame + span.frameCount) continue
      switch (span.availability) {
        case 'missing':
          return { ms, note: 'missing', bands }
        case 'filled':
          return { ms, note: 'filled', bands }
        case 'estimated':
        case 'interpolated':
          return { ms, note: 'estimated', bands }
        default:
          return { ms, note: null, bands }
      }
    }
  }
  return { ms, note: null, bands }
}

// ---------------------------------------------------------------------------
// Drawing
// ---------------------------------------------------------------------------

const BG_COLOR = 'rgba(13, 13, 18, 0.85)'
/** AYNI: the satellites' periods over the bar (fuchsia, as the sources on the globe). */
const BAND_A = 'rgba(255, 0, 204, 0.85)'
const BAND_B = 'rgba(255, 120, 230, 0.85)'
const BORDER_COLOR = 'rgba(255, 255, 255, 0.12)'
const TEXT_COLOR = '#e8eaf0'
const DIM_COLOR = 'rgba(232, 234, 240, 0.6)'
const ACCENT = 'rgba(34, 197, 94, 0.95)'
const ACCENT_FILL = 'rgba(34, 197, 94, 0.45)'
/** AYNI: the overlay strip's colours — the brand yellow, at the green's strengths. */
const OVERLAY_ACCENT = 'rgba(250, 204, 21, 0.95)'
const OVERLAY_FILL = 'rgba(250, 204, 21, 0.5)'
const OVERLAY_COVERED = 'rgba(250, 204, 21, 0.16)'
const OVERLAY_HATCH = 'rgba(250, 204, 21, 0.32)'
const OVERLAY_HOLLOW = 'rgba(250, 204, 21, 0.4)'
const REMAINDER = 'rgba(255, 255, 255, 0.07)'
const TICK_LINE = 'rgba(255, 255, 255, 0.16)'

function spanColor(availability: TimelineAvailabilitySpan['availability']): string {
  switch (availability) {
    case 'filled':
      return 'rgba(255, 196, 0, 0.30)'
    case 'missing':
      return 'rgba(255, 90, 90, 0.34)'
    case 'estimated':
    // Computed between measured frames: not a measurement either.
    case 'interpolated':
      return 'rgba(120, 200, 255, 0.28)'
    default:
      return REMAINDER
  }
}

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

/** Draw the strip. Pure canvas work: no state is kept, no clock is read. */
export function drawTimelineTrack(
  ctx: CanvasRenderingContext2D,
  state: TimelineTrackState,
  geometry: TimelineTrackGeometry,
): void {
  const { width: w, height: h, bar, compact } = geometry
  const spanMs = state.endMs - state.startMs
  const progressOf = (ms: number): number =>
    spanMs > 0 ? Math.max(0, Math.min(1, (ms - state.startMs) / spanMs)) : 0

  ctx.clearRect(0, 0, w, h)
  ctx.fillStyle = BG_COLOR
  ctx.fillRect(0, 0, w, h)
  const overlay = state.accent === 'overlay'
  const accent = overlay ? OVERLAY_ACCENT : ACCENT
  ctx.strokeStyle = state.scrubbing ? accent : BORDER_COLOR
  ctx.lineWidth = Math.max(1, 0.002 * w)
  ctx.strokeRect(ctx.lineWidth / 2, ctx.lineWidth / 2, w - ctx.lineWidth, h - ctx.lineWidth)

  ctx.textBaseline = 'middle'

  // --- Header: the axis's ends, and what one step of it is worth ---
  // Only when there is room for it; compact leaves the ends to the tick
  // labels below the bar.
  if (!compact) {
    ctx.font = String(geometry.headerFontPx) + 'px ' + MONO
    let startLabel = formatAxisEndLabel(state.startMs, spanMs, state.cadenceMs)
    let endLabel = formatAxisEndLabel(axisEndLabelMs(state), spanMs, state.cadenceMs)
    // Two ends of a short axis carry a time, which is wide; when that
    // cannot fit, the day alone is still true and still identifies the
    // range, so the form degrades before anything is dropped.
    let startW = ctx.measureText(startLabel).width
    let endW = ctx.measureText(endLabel).width
    const byPeriod = isAnnualCadence(state.cadenceMs) || isMonthlyCadence(state.cadenceMs)
    if (!byPeriod && 0.03 * w + startW + labelGapPx(w) > 0.97 * w - endW) {
      startLabel = formatTickDate(state.startMs, spanMs)
      endLabel = formatTickDate(state.endMs, spanMs)
      startW = ctx.measureText(startLabel).width
      endW = ctx.measureText(endLabel).width
    }
    const midLabel = String(state.frameCount) + ' - ' + formatCadenceShort(state.cadenceMs)
    const midW = ctx.measureText(midLabel).width
    // The ends are the axis's identity and are drawn unconditionally; the
    // middle summary is the one that yields, because a header that cannot
    // fit its own date range is worse than one without a frame count.
    const keepMid =
      w / 2 - midW / 2 >= 0.03 * w + startW + labelGapPx(w) &&
      w / 2 + midW / 2 <= 0.97 * w - endW - labelGapPx(w)
    ctx.fillStyle = TEXT_COLOR
    ctx.textAlign = 'left'
    ctx.fillText(startLabel, 0.03 * w, 0.182 * h)
    ctx.textAlign = 'right'
    ctx.fillText(endLabel, 0.97 * w, 0.182 * h)
    if (keepMid) {
      ctx.textAlign = 'center'
      ctx.fillStyle = DIM_COLOR
      ctx.fillText(midLabel, w / 2, 0.182 * h)
    }
  }

  // --- Bar ---
  ctx.fillStyle = REMAINDER
  ctx.fillRect(bar.x, bar.y, bar.w, bar.h)

  // Provenance spans first, so the elapsed fill reads over them.
  const axis = trackAxis(state)
  // A span ends where the frame after it starts (or at the axis's end).
  const spanEdge = (frame: number): number => (frame >= state.frameCount ? state.endMs : frameTimeMs(axis, frame))
  for (const span of state.availabilitySpans) {
    const from = progressOf(spanEdge(span.startFrame))
    const to = progressOf(spanEdge(span.startFrame + span.frameCount))
    if (to <= from) continue
    const x0 = bar.x + from * bar.w
    const x1 = bar.x + to * bar.w
    // A one-frame span is sub-pixel on a month-long axis; a hairline still
    // tells the viewer something happened there.
    ctx.fillStyle = spanColor(span.availability)
    ctx.fillRect(x0, bar.y, Math.max(1.5, x1 - x0), bar.h)
  }

  // --- Elapsed ---
  const playhead = progressOf(state.currentMs)
  // AYNI: an overlay strip fills only the dates its overlay covers, hatches
  // the rest, and marks the two edges, so the overlap with the green strip
  // above reads at a glance.
  const cover = state.coverage ?? null
  const c0 = cover ? progressOf(cover.startMs) : 0
  const c1 = cover ? progressOf(cover.endMs) : 1
  const covered = !cover || (state.currentMs >= cover.startMs && state.currentMs <= cover.endMs)
  if (cover) {
    ctx.save()
    ctx.beginPath()
    ctx.rect(bar.x, bar.y, bar.w, bar.h)
    ctx.clip()
    ctx.strokeStyle = overlay ? OVERLAY_HATCH : TICK_LINE
    ctx.lineWidth = 1
    const hatch = (from: number, to: number): void => {
      const x0 = bar.x + from * bar.w
      const x1 = bar.x + to * bar.w
      for (let x = x0 - bar.h; x < x1; x += 5) {
        ctx.beginPath()
        ctx.moveTo(Math.max(x, x0 - bar.h), bar.y + bar.h)
        ctx.lineTo(x + bar.h, bar.y)
        ctx.stroke()
      }
    }
    // Clipped to each uncovered run, so no stroke strays over the covered part.
    for (const [from, to] of [[0, c0], [c1, 1]] as const) {
      if (to - from <= 0) continue
      ctx.save()
      ctx.beginPath()
      ctx.rect(bar.x + from * bar.w, bar.y, (to - from) * bar.w, bar.h)
      ctx.clip()
      hatch(from, to)
      ctx.restore()
    }
    ctx.restore()
    ctx.fillStyle = overlay ? OVERLAY_COVERED : REMAINDER
    ctx.fillRect(bar.x + c0 * bar.w, bar.y, (c1 - c0) * bar.w, bar.h)
  }
  ctx.fillStyle = overlay ? OVERLAY_FILL : ACCENT_FILL
  const fillTo = Math.min(playhead, c1)
  if (fillTo > c0) ctx.fillRect(bar.x + c0 * bar.w, bar.y, (fillTo - c0) * bar.w, bar.h)
  if (cover) {
    ctx.fillStyle = accent
    for (const edge of [c0, c1]) if (edge > 0 && edge < 1) ctx.fillRect(bar.x + edge * bar.w - 0.75, bar.y, 1.5, bar.h)
  }
  // AYNI: the satellites' periods, a thin strip along the top of the bar in
  // two fuchsia tints that alternate, so a hand-over reads as a step; an
  // approximate band is drawn lighter.
  if (state.bands?.length) {
    const stripH = Math.max(2, Math.min(4, bar.h * 0.22))
    state.bands.forEach((b, i) => {
      const from = b.startMs === null ? 0 : progressOf(b.startMs)
      const to = b.endMs === null ? 1 : progressOf(b.endMs)
      if (to <= from) return
      ctx.fillStyle = i % 2 ? BAND_B : BAND_A
      if (b.approximate) ctx.globalAlpha = 0.55
      ctx.fillRect(bar.x + from * bar.w, bar.y, (to - from) * bar.w, stripH)
      ctx.globalAlpha = 1
    })
  }

  // --- Ticks and their labels, distributed as one row ---
  // The interval ladder picks a count, but a count is not a fit. This row
  // lays every candidate label out, keeps the axis's ends (in compact mode
  // they live here, at the row's extremes) and keeps an interior label
  // only where it clears everything already kept. A dropped label never
  // drops its tick line: the marks carry the rhythm, the text carries the
  // reading, and only the second one may go.
  const interval = chooseTickIntervalMs(spanMs, tickBudgetForWidth(w))
  const tickTimes = tickTimesFor(state.startMs, state.endMs, interval, state.cadenceMs)

  /** Sentinels for the two end labels, which are not ticks. */
  const END_START = -1
  const END_END = -2
  const rowY = bar.y + bar.h + (compact ? 0.16 : 0.118) * h
  const startLabel = formatAxisEndLabel(state.startMs, spanMs, state.cadenceMs)
  const endLabel = formatAxisEndLabel(axisEndLabelMs(state), spanMs, state.cadenceMs)

  ctx.font = String(geometry.tickFontPx) + 'px ' + MONO
  const tickX = (ms: number): number => bar.x + ((ms - state.startMs) / spanMs) * bar.w
  const tickLabel = (ms: number): string => formatAxisLabel(ms, interval, spanMs, state.cadenceMs)
  const boxes: TimelineLabelBox[] = tickTimes.map((ms, index) => {
    const half = ctx.measureText(tickLabel(ms)).width / 2
    const x = tickX(ms)
    return { index, left: x - half, right: x + half }
  })
  const anchored: number[] = []
  if (compact) {
    const startW = ctx.measureText(startLabel).width
    const endW = ctx.measureText(endLabel).width
    boxes.push({ index: END_START, left: bar.x, right: bar.x + startW })
    boxes.push({ index: END_END, left: bar.x + bar.w - endW, right: bar.x + bar.w })
    anchored.push(END_START, END_END)
  }
  const keep = new Set(distributeLabels(boxes, labelGapPx(w), anchored))

  ctx.fillStyle = TICK_LINE
  for (const ms of tickTimes) {
    ctx.fillRect(tickX(ms), bar.y, Math.max(1, 0.00125 * w), bar.h)
  }

  ctx.fillStyle = DIM_COLOR
  ctx.textAlign = 'center'
  for (let i = 0; i < tickTimes.length; i++) {
    // AYNI: an overlay strip's row carries its own dates; the ticks keep their lines.
    if (!keep.has(i) || (compact && cover)) continue
    const text = tickLabel(tickTimes[i]!)
    // A tick whose label reads the same as an end says nothing the row has
    // not already said — and on a weekly step the last tick often is the
    // end's own day. The tick line stays; only the echo goes.
    if (text === startLabel || text === endLabel) continue
    ctx.fillText(text, tickX(tickTimes[i]!), rowY)
  }
  if (compact && cover) {
    // AYNI: an overlay strip says the dates it covers, under them.
    const a = Math.max(cover.startMs, state.startMs)
    const b = Math.min(cover.endMs, axisEndLabelMs(state))
    const text = b > a
      ? formatAxisEndLabel(a, spanMs, state.cadenceMs) + ' – ' + formatAxisEndLabel(b, spanMs, state.cadenceMs)
      : formatAxisEndLabel(cover.startMs, spanMs, state.cadenceMs)
    const half = ctx.measureText(text).width / 2
    const mid = bar.x + ((c0 + c1) / 2) * bar.w
    ctx.fillStyle = overlay ? OVERLAY_ACCENT : DIM_COLOR
    ctx.textAlign = 'center'
    ctx.fillText(text, Math.max(bar.x + half, Math.min(bar.x + bar.w - half, mid)), rowY)
  } else if (compact) {
    ctx.textAlign = 'left'
    ctx.fillText(startLabel, bar.x, rowY)
    // Never both when they would touch: the start is the anchor.
    const startW = ctx.measureText(startLabel).width
    const endW = ctx.measureText(endLabel).width
    if (bar.x + startW + labelGapPx(w) <= bar.x + bar.w - endW) {
      ctx.textAlign = 'right'
      ctx.fillText(endLabel, bar.x + bar.w, rowY)
    }
  }

  // --- Playhead ---
  const playheadX = bar.x + playhead * bar.w
  // AYNI: hollow where the strip's subject has no data (the overlay is hidden then).
  ctx.fillStyle = covered ? accent : OVERLAY_HOLLOW
  ctx.fillRect(playheadX - 1.5, bar.y - 0.027 * h, 3, bar.h + 0.055 * h)
  ctx.beginPath()
  ctx.arc(playheadX, bar.y - 0.045 * h, Math.max(3, 0.0058 * w), 0, Math.PI * 2)
  if (covered) {
    ctx.fill()
  } else {
    ctx.strokeStyle = accent
    ctx.lineWidth = 1.2
    ctx.stroke()
  }

  // --- The instant under the playhead, clamped inside the canvas ---
  if (!compact) {
    const label = formatPlayheadLabel(state.currentMs, state.cadenceMs)
    ctx.font = 'bold ' + String(geometry.bubbleFontPx) + 'px ' + MONO
    const textWidth = ctx.measureText(label).width
    const bubbleW = textWidth + 0.0267 * w
    const bubbleX = Math.max(6, Math.min(w - bubbleW - 6, playheadX - bubbleW / 2))
    const bubbleY = 0.3 * h
    const bubbleH = 0.182 * h
    ctx.fillStyle = 'rgba(6, 12, 24, 0.92)'
    ctx.fillRect(bubbleX, bubbleY, bubbleW, bubbleH)
    ctx.strokeStyle = BORDER_COLOR
    ctx.lineWidth = 1.5
    ctx.strokeRect(bubbleX, bubbleY, bubbleW, bubbleH)
    ctx.fillStyle = TEXT_COLOR
    ctx.textAlign = 'center'
    ctx.fillText(label, bubbleX + bubbleW / 2, bubbleY + bubbleH / 2)
  }
}
